import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import { primitiveMessages } from '@/modules/nodesCanvasModule/store';
import {
  ConnectionStrategy,
  WebsocketSSHConnection,
  ResponseMessage,
  SSHMessage
} from './robotConnection';
import { Message } from '@/modules/nodesCanvasModule/components/storeClasses/message';
import { addPackageToWorkspace, V4UUID } from '@/firebase/database';
import { PackageCreator } from './packageCreator';
import { useControlCenterStore } from '../../store';
import { getAllPackagesInWorkspace } from '@/firebase/databaseDependencyInjectables';
import { Topic } from '@/modules/nodesCanvasModule/components/storeClasses/topic';
import { Service } from '@/modules/nodesCanvasModule/components/storeClasses/service';
import {
  CommandListStrategy,
  CommandObject,
  CommandResponse,
  ProcessResponseArg
} from './commandListStrategy';

export class LoadRobotPackagesStrategy extends CommandListStrategy {
  /**
   * Note: uses controlCenterStore
   * @param extraCommands
   */
  skipLoaded: boolean = false;
  finalLoad: boolean = false; // whether or not to exit loading when finished
  constructor(
    extraCommands: string,
    skipLoaded: boolean = false,
    finalLoad: boolean = false
  ) {
    super(extraCommands);
    this.skipLoaded = skipLoaded;
    this.finalLoad = finalLoad;
    this.commandList = [
      { command: 'ros2 topic list', processResponse: processTopicList }
    ];
    const store = useControlCenterStore();

    if (store.workspaceId && getAllPackagesInWorkspace(store.workspaceId)) {
      this.robotPackages = Object.entries(
        getAllPackagesInWorkspace(store.workspaceId)!
      )
        .map((obj) => obj[1])
        .filter((pkg) => pkg.robotPackage);
    }
  }

  /**
   * Function called at the end of the package creation process
   */
  finishCreation(): void {
    this.loadProgressFunc('Converting data to miros format', 99);

    // loads creation packages
    while (this.creationPackages.length !== 0) {
      this.creationPackages.reverse().forEach((pkg) => pkg.loadPackage());
      let newCreationPackages: PackageCreator[] = [];
      this.creationPackages.forEach((pkgCreator) => {
        if (
          pkgCreator
            .getExternalPackageDependencies()
            .find((pkgName) => this.getPackageFromName(pkgName))
        ) {
          newCreationPackages.push(pkgCreator);
        }
      });
      if (
        this.creationPackages.every((pkgCreator) =>
          newCreationPackages.includes(pkgCreator)
        )
      ) {
        break;
      }

      this.creationPackages = newCreationPackages;
    }
    this.loadProgressFunc('Completed', 99);
    this.robotPackages.forEach((robotPackage) =>
      addPackageToWorkspace(useControlCenterStore().workspaceId!, robotPackage)
    );
    if (this.finalLoad)
      setTimeout(() => {
        this.loadProgressFunc('', 100, true);
      }, 1000);
    super.finishCreation();
  }

  /**
   * Checks if a message with the name messageName exists.
   * if so returns its ID, else undefined
   * filters out any non alphanumeric characters
   * @param messageName
   * @returns
   */
  getMessageFromName(messageName: string): V4UUID | undefined {
    messageName = messageName.replace(/\W/g, '');
    let msg = primitiveMessages.find((primMsg: Message) => {
      return primMsg.name.toLowerCase() === messageName.toLowerCase();
    });
    if (msg) {
      return msg.id;
    }
    for (let robotPackage of this.robotPackages) {
      msg = robotPackage.msgs.find(
        (searchMsg: Message) =>
          searchMsg.name.toLowerCase() === messageName.toLowerCase()
      );
      if (msg) {
        return msg.id;
      }
    }
    for (let creationPackage of this.creationPackages) {
      msg = creationPackage.targetPackage.msgs.find((searchMsg: Message) => {
        return searchMsg.name.toLowerCase() === messageName.toLowerCase();
      });
      if (msg) {
        return msg.id;
      }
    }
    return undefined;
  }

  /**
   * Gets the message from the full message string
   * eg std_msgs/msg/int32
   * returns undefined if no message exists
   * case insensitive
   * @param messageNameString
   * @returns
   */
  getMessageFromNameString(messageNameString: string): V4UUID | undefined {
    let msg = primitiveMessages.find((primMsg: Message) => {
      return (
        primMsg.fullMessageString.toLowerCase() ===
        messageNameString.toLowerCase()
      );
    });
    if (msg) {
      return msg.id;
    }
    for (let robotPackage of this.robotPackages) {
      if (!robotPackage.msgs) continue;
      msg = robotPackage.msgs.find(
        (msg: Message) => msg.fullMessageString === messageNameString
      );
      if (msg) {
        return msg.id;
      }
    }
    return undefined;
  }

  /**
   * Takes a topic name. Topic name can be in
   * @param topicName
   * @returns
   */
  getTopicFromName(topicName: string): V4UUID | undefined {
    // if package format
    if (topicName.includes('/') && topicName.split('/')[0] !== '') {
      let srvPackage = this.getPackageFromName(topicName.split('/')[0]);
      topicName = topicName.split('/').reverse()[0];
      if (!srvPackage) return undefined;
      let topic = srvPackage.topics.find(
        (topic) => topic.name.toLowerCase() === topicName.toLowerCase()
      );
      return (topic && topic.id) || undefined;
    }

    let topic = undefined;
    for (let robotPackage of this.robotPackages) {
      if (!robotPackage.topics) continue;
      topic = robotPackage.topics.find(
        (topic: Topic) => topic.name.toLowerCase() === topicName.toLowerCase()
      );
      if (topic) {
        return topic.id;
      }
    }
    return undefined;
  }

  /**
   * takes in a service name. Service name can be a straight name,
   * or contain package details with /
   * @param serviceName
   * @returns
   */
  getServiceFromName(serviceName: string): V4UUID | undefined {
    // if package format
    if (serviceName.includes('/')) {
      let srvPackage = this.getPackageFromName(serviceName.split('/')[0]);
      serviceName = serviceName.split('/').reverse()[0];

      return (
        (
          srvPackage &&
          srvPackage.services.find(
            (service) =>
              service.name.toLowerCase() === serviceName.toLowerCase()
          )
        )?.id || undefined
      );
    }

    let service = undefined;
    for (let robotPackage of this.robotPackages) {
      if (!robotPackage.services) continue;
      service = robotPackage.services.find(
        (service: Service) =>
          service.name.toLowerCase() === serviceName.toLowerCase()
      );
      if (service) {
        return service.id;
      }
    }
    return undefined;
  }

  getPackageFromName(packageName: string): PackageT | undefined {
    return this.robotPackages.find(
      (robotPackage: PackageT) => robotPackage.name === packageName
    );
  }
  /** Adds a new package to track in the package list and returns that package */
  addNewPackage(packageName: string): PackageT {
    let newPkg = new PackageT(packageName);
    newPkg.robotPackage = true;
    this.robotPackages.push(newPkg);
    return newPkg;
  }

  getPackageCreator(targetPackage: PackageT): PackageCreator | undefined {
    return this.creationPackages.find(
      (pkgCreator) => pkgCreator.targetPackage.id === targetPackage.id
    );
  }

  addPackageCreator(targetPackage: PackageT): PackageCreator {
    const packageCreator = new PackageCreator(targetPackage, this);
    this.creationPackages.push(packageCreator);
    return packageCreator;
  }
}

const processTopicList = (args: ProcessResponseArg) => {
  let topicCommands: Array<CommandObject> = [];
  args.strategy.setProgress('Loading topic list');
  for (let topicName of args.response.trim().split('\n')) {
    topicCommands.push({
      command: `ros2 topic info ${topicName}`,
      processResponse: processTopicInfo,
      extraInfo: { topicName: topicName }
    });
  }
  args.strategy.addCommands(topicCommands);
  return CommandResponse.SUCCESS;
};

const processTopicInfo = (args: ProcessResponseArg) => {
  args.strategy.setProgress('Loading topic info');
  const msgType = args.response.split('\n')[0].slice(6); // first line, remove 'Type: '
  const msgPkgName = msgType.split('/')[0];
  let msgPkg: PackageT | undefined = (
    args.strategy as LoadRobotPackagesStrategy
  ).getPackageFromName(msgPkgName);
  if (msgPkg && (args.strategy as LoadRobotPackagesStrategy).skipLoaded)
    return CommandResponse.SKIPPED;
  else if (!msgPkg) {
    msgPkg = (args.strategy as LoadRobotPackagesStrategy).addNewPackage(
      msgPkgName
    );
  }

  let pkgCreator = (
    args.strategy as LoadRobotPackagesStrategy
  ).getPackageCreator(msgPkg);
  // if no package creator, load a new one
  if (!pkgCreator) {
    pkgCreator = (args.strategy as LoadRobotPackagesStrategy).addPackageCreator(
      msgPkg
    );
    args.strategy.addCommands([
      {
        command: `ros2 interface package ${msgPkg.name}`,
        processResponse: loadPkgMsgs,
        extraInfo: {
          pkgCreator: pkgCreator
        }
      }
    ]);
  }

  // add topic into target package creator
  pkgCreator.topicsToLoad.push({
    topic: args.extraInfo.topicName,
    type: msgType
  });

  return CommandResponse.SUCCESS;
};

const loadPkgMsgs = (args: ProcessResponseArg): CommandResponse => {
  args.strategy.setProgress('Getting message info');
  args.strategy.addCommands(
    args.response
      .trim()
      .split('\n')
      .filter((interaceStr) => {
        return interaceStr.trim().length > 0;
      })
      .map((interfaceStr) => {
        return {
          command: `ros2 interface show ${interfaceStr}`,
          processResponse: addInterface,
          extraInfo: {
            pkgCreator: args.extraInfo.pkgCreator,
            interfaceName: interfaceStr
          }
        };
      })
  );
  return CommandResponse.SUCCESS;
};

// adds an interface to the package creator
// checks if the interface references any external unloaded packages and loads them
const addInterface = (args: ProcessResponseArg): CommandResponse => {
  let interfaceType = args.extraInfo.interfaceName.split('/')[1];
  let interfaceDef = {
    nameString: args.extraInfo.interfaceName,
    definition: args.response
  };
  if (interfaceType === 'msg') {
    args.strategy.setProgress(`Adding message ${interfaceDef.nameString}`);
    args.extraInfo.pkgCreator.msgsDefinitions.push(interfaceDef);
  } else if (interfaceType === 'srv') {
    args.strategy.setProgress(`Adding service ${interfaceDef.nameString}`);
    args.extraInfo.pkgCreator.srvDefinitions.push(interfaceDef);
  }
  if (!interfaceDef.definition) return CommandResponse.SKIPPED;

  // checking for external packages
  interfaceDef.definition.split('\n').forEach((paramString) => {
    if (
      paramString.length !== 0 &&
      paramString[0] !== '\t' &&
      paramString.trim()[0] !== '#' &&
      paramString.trim().split(' ')[0].includes('/')
    ) {
      let pkgName = paramString.trim().split('/')[0];
      let msgName = paramString.trim().split(' ')[0].split('/')[1];
      if (
        !(args.strategy as LoadRobotPackagesStrategy).getMessageFromName(
          msgName
        )
      ) {
        let pkg =
          (args.strategy as LoadRobotPackagesStrategy).getPackageFromName(
            pkgName
          ) ||
          (args.strategy as LoadRobotPackagesStrategy).addNewPackage(pkgName);

        let pkgCreator = (
          args.strategy as LoadRobotPackagesStrategy
        ).getPackageCreator(pkg);
        if (!pkgCreator) {
          pkgCreator = (
            args.strategy as LoadRobotPackagesStrategy
          ).addPackageCreator(pkg);
          args.strategy.addCommands([
            {
              command: `ros2 interface package ${pkgName}`,
              processResponse: loadPkgMsgs,
              extraInfo: {
                pkgCreator: pkgCreator
              }
            }
          ]);
        }
      }
    }
  });
  return CommandResponse.SUCCESS;
};
